"use client";
import { useState } from "react";
import { useUserContext } from "@/context/UserContext";
import { createPersonalCollection } from "@/services/PersonalCollectionService";
import {
  CollectionStatus,
  BasicPersonalCollection,
} from "@/interfaces/personal-collections/personal-collections.interface";
import { notifyError, notifySuccess } from "@/utils/NotificationFunctions";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: (collection: BasicPersonalCollection) => void;
};

export default function NewPersonalCollectionModal({ open, onClose, onCreated }: Props) {
  const { currentUser, userToken } = useUserContext();
  const [name, setName] = useState("");
  const [status, setStatus] = useState<CollectionStatus>("PRIVATE");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleClose = () => {
    if (submitting) return;
    setName("");
    setStatus("PRIVATE");
    onClose();
  };

  const handleCreate = async () => {
    if (!currentUser?._id) {
      notifyError("You must be logged in.");
      return;
    }
    const trimmed = name.trim();
    if (!trimmed) {
      notifyError("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createPersonalCollection(
        { name: trimmed, status, userId: currentUser._id },
        userToken
      );
      if (res && res.status === "Success") {
        notifySuccess("Collection created");
        onCreated?.(res.data);
        handleClose();
      } else {
        notifyError("Failed to create collection");
      }
    } catch (e) {
      notifyError("Connection error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />

      <div className="relative w-full max-w-2xl rounded-xl bg-[#23262f] text-white shadow-xl ring-1 ring-white/10">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold">Create Personal Collection</h2>
          <button
            onClick={handleClose}
            className="px-2 py-1 text-sm rounded-md hover:bg-white/10"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label htmlFor="collection-name" className="block text-sm mb-1">
              Name
            </label>
            <input
              id="collection-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Collection name"
              className="w-full rounded-md border border-white/20 bg-transparent px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30"
            />
          </div>

          <div>
            <label className="block text-sm mt-4 mb-1">Status</label>
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="status"
                  value="PRIVATE"
                  checked={status === "PRIVATE"}
                  onChange={() => setStatus("PRIVATE")}
                  className="h-4 w-4"
                />
                <span className="inline-flex items-center rounded-md bg-yellow-400/10 px-2 py-1 text-xs font-medium text-yellow-500 inset-ring inset-ring-yellow-400/20">
                  Private
                </span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="status"
                  value="PUBLIC"
                  checked={status === "PUBLIC"}
                  onChange={() => setStatus("PUBLIC")}
                  className="h-4 w-4"
                />
                <span className="inline-flex items-center rounded-md bg-blue-400/10 px-2 py-1 text-xs font-medium text-blue-400 inset-ring inset-ring-blue-400/30">
                  Public
                </span>
              </label>
            </div>
          </div>

          {/** User id is passed internally; no UI field needed. */}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-white/10">
          <button
            onClick={handleClose}
            className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm bg-white/10 hover:bg-white/15"
            style={{ borderRadius: "6px" }}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm text-white bg-[linear-gradient(137.48deg,rgba(255,83,188,0.25)_15.13%,rgba(10,252,212,0.25)_87.01%)] hover:opacity-90"
            style={{ borderRadius: "6px" }}
            disabled={submitting}
          >
            {submitting ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
